import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { createMovement, listProducts } from '../../api/stock'
import { getMyPractices } from '../../api/staff'
import { useToast } from '../../components/ui/Toast'
import { FormField, inputClass, selectClass } from '../../components/ui/FormField'
import { NumberStepper } from '../../components/ui/NumberStepper'
import { Button } from '../../components/ui/Button'
import { Card, ErrorBox, SectionTitle } from '../../components/ui/Card'
import { useFetch } from '../../hooks/useFetch'
import type { ProductListItem } from '../../types/stock'

interface Props {
  practiceId: number
  consultationId?: number
  patientId?: number
  onDispatched: () => void
  onCancel: () => void
}

export function DispenseForm({ practiceId, consultationId, patientId, onDispatched, onCancel }: Props) {
  const toast = useToast()
  const [products, setProducts] = useState<ProductListItem[]>([])
  const [productId, setProductId] = useState('')
  const [quantity, setQuantity] = useState('')
  const [notes, setNotes] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    listProducts({ practice_id: practiceId, is_active: true })
      .then(setProducts)
      .catch(() => setError('Failed to load products'))
  }, [practiceId])

  const selectedProduct = products.find(p => String(p.id) === productId)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!productId || !quantity) {
      setError('Product and quantity are required.')
      return
    }
    const qty = parseFloat(quantity)
    if (isNaN(qty) || qty <= 0) {
      setError('Quantity must be greater than zero.')
      return
    }
    if (selectedProduct && qty > parseFloat(selectedProduct.total_on_hand)) {
      setError(`Quantity exceeds stock on hand (${selectedProduct.total_on_hand} ${selectedProduct.unit}).`)
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      await createMovement({
        product_id: Number(productId),
        practice_id: practiceId,
        movement_type: 'dispense',
        quantity: String(qty),
        patient_id: patientId ?? null,
        consultation_id: consultationId ?? null,
        notes: notes || null,
      })
      toast.success('Dispensed successfully')
      onDispatched()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to dispense')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={e => void handleSubmit(e)} className="flex flex-col gap-3">
      {error && <ErrorBox>{error}</ErrorBox>}

      <FormField label="Product" required>
        <select
          value={productId}
          onChange={e => { setProductId(e.target.value); setError(null) }}
          className={selectClass()}
        >
          <option value="">Select product…</option>
          {products.map(p => (
            <option key={p.id} value={String(p.id)}>
              {p.name}{p.schedule ? ` (Sch ${p.schedule})` : ''} — {p.total_on_hand} {p.unit} on hand
            </option>
          ))}
        </select>
      </FormField>

      <FormField
        label="Quantity"
        required
        hint={selectedProduct ? `${selectedProduct.total_on_hand} ${selectedProduct.unit} on hand` : undefined}
      >
        <NumberStepper
          step={0.001}
          min={0.001}
          value={quantity}
          onChange={v => { setQuantity(v); setError(null) }}
          placeholder="0.000"
        />
      </FormField>

      <FormField label="Notes">
        <input
          type="text"
          value={notes}
          onChange={e => setNotes(e.target.value)}
          placeholder="Optional notes"
          className={inputClass()}
        />
      </FormField>

      <div className="flex gap-2">
        <Button type="submit" variant="primary" size="sm" disabled={submitting}>
          {submitting ? 'Dispensing…' : 'Dispense'}
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  )
}

export default function StandaloneDispensePage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const patientId = searchParams.get('patient_id') ? Number(searchParams.get('patient_id')) : undefined

  const { data: practices, loading, error } = useFetch(() => getMyPractices(), [])
  const practiceId = practices?.[0]?.id

  if (loading) return <div className="text-text-2 p-8">Loading…</div>
  if (error || !practices || practices.length === 0) {
    return <div className="p-8"><ErrorBox>No practices found.</ErrorBox></div>
  }
  if (!practiceId) return null

  return (
    <div className="p-8 bg-bg min-h-screen">
      <h1 className="m-0 mb-7 text-[1.4rem] font-bold text-text-heading">
        Dispense Drug
      </h1>
      <Card className="max-w-[560px]">
        <SectionTitle>Dispense Details</SectionTitle>
        <DispenseForm
          practiceId={practiceId}
          patientId={patientId}
          onDispatched={() => void navigate('/stock')}
          onCancel={() => void navigate(-1)}
        />
      </Card>
    </div>
  )
}
